import { useState, useEffect, useRef, useCallback } from 'react';
import { Save, Eye, EyeOff, FileText, Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { taskApi, documentApi } from '@/services/api';
import { useNotificationStore } from '@/stores';
import mammoth from 'mammoth';
import type { TemplateItem, FieldValue } from '@/components/TemplatesPage';

/**
 * Parse USPTO ST96 claims XML into formatted HTML.
 * Handles <uspat:Claim> / <uspat:ClaimText> / <uspat:ClaimReference> elements.
 */
function parseClaimsXml(xml: string): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'text/xml');

    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) return '';

    // Find all Claim elements (handle namespaced and non-namespaced)
    const claims = doc.querySelectorAll('Claim');
    if (claims.length === 0) return '';

    let html = '<div style="margin: 1em 0;"><h3 style="font-weight: bold; margin-bottom: 0.5em; text-decoration: underline;">CLAIMS</h3>';

    claims.forEach((claim) => {
      const textNodes = claim.querySelectorAll('ClaimText');
      if (textNodes.length === 0) return;

      html += '<div style="margin-bottom: 0.75em;">';
      textNodes.forEach((textNode, idx) => {
        // Get text content, preserving claim reference numbers
        let text = '';
        textNode.childNodes.forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE) {
            text += child.textContent;
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            // ClaimReference or other inline elements — just use text content
            text += (child as Element).textContent;
          }
        });
        text = text.trim();
        if (!text) return;

        if (idx === 0) {
          // First line is the main claim text (includes claim number)
          html += `<p style="margin: 0 0 0.25em 0; text-indent: 2em;">${escapeHtml(text)}</p>`;
        } else {
          // Subsequent lines are sub-elements, indented further
          html += `<p style="margin: 0 0 0.15em 0; padding-left: 4em;">${escapeHtml(text)}</p>`;
        }
      });
      html += '</div>';
    });

    html += '</div>';
    return html;
  } catch {
    return '';
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Post-process HTML from mammoth to convert any embedded USPTO XML
 * (claims, office actions, etc.) into readable formatted text.
 */
function postProcessHtml(html: string): string {
  // Match XML blocks that contain USPTO claims data
  // These appear as text in the HTML since mammoth doesn't understand them
  const xmlPattern = /(&lt;\?xml[^]*?&lt;\/uspat:ClaimsDocument&gt;|<\?xml[^]*?<\/uspat:ClaimsDocument>)/g;

  let result = html;

  // Also handle cases where the XML is HTML-escaped (mammoth output)
  const escapedXmlPattern = /(&lt;\?xml[\s\S]*?&lt;\/uspat:ClaimsDocument&gt;)/g;
  const matches = result.match(escapedXmlPattern);
  if (matches) {
    for (const match of matches) {
      // Unescape the HTML entities back to XML
      const rawXml = match
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"');
      const formatted = parseClaimsXml(rawXml);
      if (formatted) {
        result = result.replace(match, formatted);
      }
    }
  }

  // Also handle raw (unescaped) XML if present
  const rawMatches = result.match(/<\?xml[\s\S]*?<\/uspat:ClaimsDocument>/g);
  if (rawMatches) {
    for (const match of rawMatches) {
      const formatted = parseClaimsXml(match);
      if (formatted) {
        result = result.replace(match, formatted);
      }
    }
  }

  return result;
}

/** Strip {{ }} wrapper from a key, if present. */
function stripBraces(key: string): string {
  return key.replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '');
}

/** Ensure a key is wrapped with {{ }}. */
function wrapBraces(key: string): string {
  const stripped = stripBraces(key);
  return stripped ? `{{${stripped}}}` : '';
}

/** Convert an entries array back to a Record, auto-wrapping keys. */
function entriesToRecord(entries: [string, FieldValue][]): Record<string, FieldValue> {
  const record: Record<string, FieldValue> = {};
  for (const [key, value] of entries) {
    const wrapped = wrapBraces(key);
    if (wrapped) record[wrapped] = value;
  }
  return record;
}

/** Normalize a mapping value to FieldValue format. Handles both string and object values. */
function normalizeFieldValue(value: string | FieldValue | unknown): FieldValue {
  if (typeof value === 'object' && value !== null && 'description' in (value as any)) {
    const obj = value as FieldValue;
    return { description: obj.description || '', example: obj.example || '' };
  }
  if (typeof value === 'string') {
    return { description: value, example: '' };
  }
  return { description: '', example: '' };
}

const RESPONSE_TYPE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'non-final', label: 'Non-Final OA Response' },
  { value: 'final', label: 'Final OA Response' },
  { value: 'restriction-election', label: 'Restriction/Election Response' },
  { value: 'preliminary-amendment', label: 'Preliminary Amendment' },
  { value: 'general-transmittal', label: 'General Transmittal' },
  { value: 'ex-parte-quayle', label: 'Response to Ex Parte Quayle' },
  { value: 'notice-of-allowance', label: 'Comments on Notice of Allowance' },
  { value: 'correct-application-papers', label: 'Response to Notice of Correct Application Papers' },
] as const;

interface TemplateDocxViewerProps {
  selectedTemplate: TemplateItem | null;
  onSaveSuccess: () => void;
}

export function TemplateDocxViewer({ selectedTemplate, onSaveSuccess }: TemplateDocxViewerProps) {
  const { addNotification } = useNotificationStore();
  const contentRef = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const [showFields, setShowFields] = useState(false);

  // Editable mapping state: arrays of [key, FieldValue] tuples
  const [editedDynamicFields, setEditedDynamicFields] = useState<[string, FieldValue][]>([]);
  const [editedSections, setEditedSections] = useState<[string, FieldValue][]>([]);
  const [responseType, setResponseType] = useState<string>('');

  // Initialise editable state from selectedTemplate.mapping, normalizing to FieldValue format
  useEffect(() => {
    const mapping = selectedTemplate?.mapping;
    setEditedDynamicFields(
      mapping?.DynamicFields
        ? Object.entries(mapping.DynamicFields).map(([k, v]) => [k, normalizeFieldValue(v)] as [string, FieldValue])
        : []
    );
    setEditedSections(
      mapping?.Sections
        ? Object.entries(mapping.Sections).map(([k, v]) => [k, normalizeFieldValue(v)] as [string, FieldValue])
        : []
    );
    setResponseType(selectedTemplate?.response_type || '');
  }, [selectedTemplate]);

  const handleResponseTypeChange = async (value: string) => {
    if (!selectedTemplate) return;
    const newValue = value === 'none' ? '' : value;
    setResponseType(newValue);
    try {
      await taskApi.updateTemplateResponseType(selectedTemplate.template_id, newValue);
      addNotification({ type: 'success', title: 'Updated', message: 'Response type saved.' });
      onSaveSuccess();
    } catch {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to update response type.' });
    }
  };

  const loadTemplate = useCallback(async () => {
    if (!selectedTemplate) {
      setHtml('');
      return;
    }

    try {
      setLoading(true);
      const response = await taskApi.getTemplate(selectedTemplate.template_id, selectedTemplate.template_name);
      if (!response?.data) throw new Error('Invalid response');

      const arrayBuffer = await (response.data as Blob).arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setHtml(postProcessHtml(result.value));
    } catch (error) {
      console.error('Error loading template:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to load template document.' });
      setHtml('');
    } finally {
      setLoading(false);
    }
  }, [selectedTemplate, addNotification]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleSave = async () => {
    if (!selectedTemplate || !contentRef.current) return;

    try {
      setSaving(true);
      const editedHtml = contentRef.current.innerHTML;

      const response = await taskApi.updateTemplate(selectedTemplate.template_id, editedHtml);

      if (response?.job_id) {
        taskApi.startGraduatedPolling(
          async () => {
            const result = await documentApi.getJobStatus(response.job_id);
            if (result?.status?.toLowerCase().startsWith('complete')) return result;
            if (result?.status?.toLowerCase().includes('error')) return result;
            return null;
          },
          (result) => {
            if (result?.status?.toLowerCase().startsWith('complete')) {
              addNotification({ type: 'success', title: 'Saved', message: 'Template saved successfully.' });
              onSaveSuccess();
            } else {
              addNotification({ type: 'error', title: 'Error', message: 'Failed to save template.' });
            }
            setSaving(false);
          },
          (error) => {
            console.error('Save polling error:', error);
            addNotification({ type: 'error', title: 'Error', message: 'Failed to save template.' });
            setSaving(false);
          }
        );
      } else {
        addNotification({ type: 'success', title: 'Saved', message: 'Template updated.' });
        onSaveSuccess();
        setSaving(false);
      }
    } catch (error) {
      console.error('Save error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to save template.' });
      setSaving(false);
    }
  };

  const handleSaveFields = async () => {
    if (!selectedTemplate) return;

    try {
      setSavingFields(true);
      const mapping: { DynamicFields?: Record<string, FieldValue>; Sections?: Record<string, FieldValue> } = {};
      if (editedDynamicFields.length > 0) mapping.DynamicFields = entriesToRecord(editedDynamicFields);
      if (editedSections.length > 0) mapping.Sections = entriesToRecord(editedSections);

      const response = await taskApi.updateTemplateMapping(selectedTemplate.template_id, mapping);

      if (response?.job_id) {
        taskApi.startGraduatedPolling(
          async () => {
            const result = await documentApi.getJobStatus(response.job_id);
            if (result?.status?.toLowerCase().startsWith('complete')) return result;
            if (result?.status?.toLowerCase().includes('error')) return result;
            return null;
          },
          (result) => {
            if (result?.status?.toLowerCase().startsWith('complete')) {
              addNotification({ type: 'success', title: 'Fields Saved', message: 'Template fields updated successfully.' });
              onSaveSuccess();
            } else {
              addNotification({ type: 'error', title: 'Error', message: 'Failed to save template fields.' });
            }
            setSavingFields(false);
          },
          (error) => {
            console.error('Save fields polling error:', error);
            addNotification({ type: 'error', title: 'Error', message: 'Failed to save template fields.' });
            setSavingFields(false);
          }
        );
      } else {
        addNotification({ type: 'success', title: 'Fields Saved', message: 'Template fields updated.' });
        onSaveSuccess();
        setSavingFields(false);
      }
    } catch (error) {
      console.error('Save fields error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to save template fields.' });
      setSavingFields(false);
    }
  };

  // --- Field editing helpers ---
  const updateFieldKey = (
    setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>,
    index: number,
    newKey: string,
  ) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = [newKey, updated[index][1]];
      return updated;
    });
  };

  const updateFieldProp = (
    setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>,
    index: number,
    prop: 'description' | 'example',
    newValue: string,
  ) => {
    setter((prev) => {
      const updated = [...prev];
      const fieldValue = { ...updated[index][1], [prop]: newValue };
      updated[index] = [updated[index][0], fieldValue];
      return updated;
    });
  };

  const removeField = (
    setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>,
    index: number,
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const addField = (
    setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>,
  ) => {
    setter((prev) => [...prev, ['', { description: '', example: '' }]]);
  };

  if (!selectedTemplate) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-400">
        <FileText className="w-10 h-10 mb-2 opacity-50" />
        <p className="text-sm">Select a template to view</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        <p className="mt-2 text-sm text-slate-500">Loading template...</p>
      </div>
    );
  }

  const hasFields = editedDynamicFields.length > 0 || editedSections.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-200 bg-white/60">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-600 truncate max-w-[200px]" title={selectedTemplate.template_name}>
            {selectedTemplate.template_name.split('/').pop()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-xs h-7"
            onClick={() => setShowFields(!showFields)}
          >
            {showFields ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showFields ? 'Hide Fields' : 'Show Fields'}
          </Button>
          <Button
            size="sm"
            className="gap-1 text-xs h-7 bg-teal-600 hover:bg-teal-700"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </Button>
        </div>
      </div>

      {/* Response Type Selector */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-200 bg-white/40">
        <span className="text-xs font-medium text-slate-500 whitespace-nowrap">Response Type:</span>
        <Select value={responseType || 'none'} onValueChange={handleResponseTypeChange}>
          <SelectTrigger className="h-7 text-xs flex-1 max-w-[300px]">
            <SelectValue placeholder="Select response type..." />
          </SelectTrigger>
          <SelectContent className="max-h-[min(60vh,320px)] overflow-y-auto">
            {RESPONSE_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value || 'none'} value={opt.value || 'none'} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Fields Panel */}
      {showFields && (
        <div className="border-b border-slate-200 bg-slate-50 p-2 max-h-[300px] overflow-y-auto">
          {/* Dynamic Fields section */}
          <div className="mb-2">
            <div className="text-xs font-semibold text-slate-600 mb-1">Dynamic Fields</div>
            <div className="space-y-1.5">
              {editedDynamicFields.map(([key, fieldVal], idx) => (
                <div key={idx} className="flex gap-1">
                  <div className="flex-[2] flex items-start min-w-0">
                    <div className="flex items-center bg-white rounded border border-slate-200 focus-within:ring-1 focus-within:ring-teal-400 w-full">
                      <span className="text-xs font-medium text-slate-400 pl-1.5 select-none">{'{{'}</span>
                      <input
                        type="text"
                        className="flex-1 text-xs px-0.5 py-1 font-medium text-teal-700 focus:outline-none bg-transparent min-w-0"
                        value={stripBraces(key)}
                        placeholder="field_name"
                        onChange={(e) => updateFieldKey(setEditedDynamicFields, idx, e.target.value)}
                      />
                      <span className="text-xs font-medium text-slate-400 pr-1.5 select-none">{'}}'}</span>
                    </div>
                  </div>
                  <div className="flex-[3] flex flex-col gap-0.5 min-w-0">
                    <input
                      type="text"
                      className="text-xs bg-white rounded px-2 py-1 border border-slate-200 text-slate-600 focus:outline-none focus:ring-1 focus:ring-teal-400 min-w-0"
                      value={fieldVal.description}
                      placeholder="Description"
                      onChange={(e) => updateFieldProp(setEditedDynamicFields, idx, 'description', e.target.value)}
                    />
                    <input
                      type="text"
                      className="text-xs bg-white rounded px-2 py-1 border border-slate-200 text-slate-400 italic focus:outline-none focus:ring-1 focus:ring-teal-400 min-w-0"
                      value={fieldVal.example}
                      placeholder="Example value"
                      onChange={(e) => updateFieldProp(setEditedDynamicFields, idx, 'example', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="p-0.5 text-slate-400 hover:text-red-500 flex-shrink-0 mt-1"
                    onClick={() => removeField(setEditedDynamicFields, idx)}
                    title="Remove field"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-1 flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
              onClick={() => addField(setEditedDynamicFields)}
            >
              <Plus className="w-3 h-3" /> Add Field
            </button>
          </div>

          {/* Sections */}
          <div className="mb-2">
            <div className="text-xs font-semibold text-slate-600 mb-1">Sections</div>
            <div className="space-y-1.5">
              {editedSections.map(([key, fieldVal], idx) => (
                <div key={idx} className="flex gap-1">
                  <div className="flex-[2] flex items-start min-w-0">
                    <div className="flex items-center bg-white rounded border border-slate-200 focus-within:ring-1 focus-within:ring-purple-400 w-full">
                      <span className="text-xs font-medium text-slate-400 pl-1.5 select-none">{'{{'}</span>
                      <input
                        type="text"
                        className="flex-1 text-xs px-0.5 py-1 font-medium text-purple-700 focus:outline-none bg-transparent min-w-0"
                        value={stripBraces(key)}
                        placeholder="section_name"
                        onChange={(e) => updateFieldKey(setEditedSections, idx, e.target.value)}
                      />
                      <span className="text-xs font-medium text-slate-400 pr-1.5 select-none">{'}}'}</span>
                    </div>
                  </div>
                  <div className="flex-[3] flex flex-col gap-0.5 min-w-0">
                    <input
                      type="text"
                      className="text-xs bg-white rounded px-2 py-1 border border-slate-200 text-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-400 min-w-0"
                      value={fieldVal.description}
                      placeholder="Description"
                      onChange={(e) => updateFieldProp(setEditedSections, idx, 'description', e.target.value)}
                    />
                    <textarea
                      className="text-xs bg-white rounded px-2 py-1 border border-slate-200 text-slate-400 italic focus:outline-none focus:ring-1 focus:ring-purple-400 min-w-0 resize-y"
                      rows={1}
                      value={fieldVal.example}
                      placeholder="Example content"
                      onChange={(e) => updateFieldProp(setEditedSections, idx, 'example', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="p-0.5 text-slate-400 hover:text-red-500 flex-shrink-0 mt-1"
                    onClick={() => removeField(setEditedSections, idx)}
                    title="Remove section"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-1 flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
              onClick={() => addField(setEditedSections)}
            >
              <Plus className="w-3 h-3" /> Add Section
            </button>
          </div>

          {/* Save Fields button */}
          <div className="flex justify-end pt-1 border-t border-slate-200">
            <Button
              size="sm"
              className="gap-1 text-xs h-7 bg-teal-600 hover:bg-teal-700"
              onClick={handleSaveFields}
              disabled={savingFields}
            >
              {savingFields ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save Fields
            </Button>
          </div>
        </div>
      )}

      {/* DOCX Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div
          ref={contentRef}
          className="p-4 prose prose-sm max-w-none focus:outline-none"
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}
